<template>
    <div class="p-10">
        <div class="display-flex" style="align-items: stretch">
            <!--Table overview-->
            <div style="flex: 1">
                <div class="label">Overview table</div>
                <LoadingBox v-if="isLoadingOverviewTable" :height="444"></LoadingBox>
                <div v-else>
                    <table class="table-overview" v-if="overviewData.batches && overviewData.batches.total > 0">
                        <tr v-for="(val, title) in overviewData" :key="title">
                            <th>{{ $utils.convertSnakeToCapitalize(title) }}</th>
                            <td>
                                <table class="table-inner">
                                    <tr v-for="(value, key) in val" :key="key">
                                        <th style="text-align: left">{{ $utils.convertSnakeToCapitalize(key) }}</th>
                                        <td style="width: 50px; text-align: center">{{ value }}</td>
                                    </tr>
                                </table>
                            </td>
                        </tr>
                    </table>
                    <div v-else class="centerXY empty-box" style="height: 444px">
                        <img src="/img/empty-box.png" style="width: 180px" class="mb-10">
                        {{ $f.tc('noDataAvailable') }}
                    </div>
                </div>
            </div>
            <!--Pending tasks chart-->
            <div class="pending-tasks-chart">
                <div class="display-flex justify-space-between align-center mb-6">
                    <div class="label mb-0">Pending tasks</div>
                    <div style="width: 400px">
                        <v-autocomplete
                            hide-details outlined
                            :items="batches"
                            item-text="name"
                            item-value="id"
                            v-model="batchActive"
                            class="input-custom"
                        ></v-autocomplete>
                    </div>
                </div>
                <div>
                    <LoadingBox v-if="isLoadingLineChart" :height="444"></LoadingBox>
                    <div v-else>
                        <LineChart
                            :height="['admin','qc_lead'].includes(permission)?444:323"
                            :data="lineChartData"
                            :xAxesGridLines="false"
                            :yAxesGridLines="false"
                            ref="LineChartPendingTasks"
                            v-if="lineChartData.datasets.length > 0"
                        ></LineChart>
                        <div v-else class="centerXY empty-box" style="height: 444px">
                            <img src="/img/empty-box.png" style="width: 180px" class="mb-10">
                            {{ $f.tc('noDataAvailable') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="['admin','qc_lead'].includes(permission)">
            <!--Result QC campaign-->
            <div class="display-flex justify-space-between align-center mb-6" style="margin-top: 20px">
                <v-menu :nudge-width="100" nudge-bottom="43">
                    <template v-slot:activator="{ on }">
                        <div class="display-flex align-center" v-on="on">
                            <div class="label mb-0">
                                {{ resultQCCampaignChartActive.name }}
                            </div>
                            <img class="ml-8" src="/img/select_grey.svg" alt="icon-select-chart"
                                 style="cursor: pointer">
                        </div>
                    </template>
                    <v-list>
                        <v-list-tile
                            v-for="item in resultQCCampaignCharts"
                            :key="item.id"
                            @click="resultQCCampaignChartActive = item"
                            :class="{'active-chart': resultQCCampaignChartActive.id === item.id}"
                        >
                            <v-list-tile-title
                                v-text="item.name"
                            ></v-list-tile-title>
                        </v-list-tile>
                    </v-list>
                </v-menu>
                <div class="display-flex align-center">
                    <div style="width: 200px">
                        <v-select
                            hide-details outlined
                            :items="timeFrameOptions"
                            item-text="text"
                            item-value="value"
                            v-model="filter.timeFrame"
                            class="input-custom"
                            v-if="resultQCCampaignChartActive.type === 1"
                        ></v-select>
                    </div>
                    <div style="width: 200px" class="ml-8">
                        <v-select
                            hide-details outlined
                            :items="criteriaList"
                            item-text="name"
                            item-value="id"
                            v-model="filter.criteriaId"
                            class="input-custom"
                        ></v-select>
                    </div>
                </div>
            </div>
            <LoadingBox v-if="isLoadingStackBarChart" :height="400"></LoadingBox>
            <stack-bar-chart
                v-if="stackBarChartData.labels.length !== 0"
                :data="stackBarChartData"
                :title-left="'abc'"
                :y-axis-ticks="true"
                :maxStep="2"
                :legendLabel="legendLabelStackBarChart"
                ref="StackBarChartResultQC"
            ></stack-bar-chart>
            <div v-if="stackBarChartData.labels.length === 0 && !isLoadingStackBarChart" class="centerXY empty-box"
                 style="height: 400px">
                <img src="/img/empty-box.png" style="width: 180px" class="mb-10">
                {{ $f.tc('noDataAvailable') }}
            </div>
        </div>
    </div>
</template>
<script>
import RoleMixin from '@/modules/fptai/mixins/RoleMixin'
import LineChart from "@/components/chart/LineChart";
import StackBarChart from "@/components/chart/StackBarChart";
import QcCampaignService from "@/services/qc.campaign.service";
import {mapState} from "vuex";
import LoadingBox from "@/components/LoadingBox";
import $const from "@/utils/const";

const legendLabelStackBarChart = {
    display: true,
    labels: {
        boxWidth: 12,
        usePointStyle: true,
        fontSize: 14,
        fontColor: '#8A97AA',
        padding: 24,
        lineWidth: 0
    },
    title: {
        padding: {
            left: 20
        }
    }
}
const fixColorResultQCCampaign = ['#225ee8', '#bde2fd', '#7438fe', '#cea9fe', '#3eae30', '#a1ef4b', '#ff3d00', '#ff8660', '#f3931b', '#f3931b']

export default {
    name: 'QCCampaignOverview',
    mixins: [RoleMixin],
    components: {
        LoadingBox,
        StackBarChart,
        LineChart,
    },
    data() {
        return {
            legendLabelStackBarChart,
            fixColorResultQCCampaign,
            overviewData: {},
            lineChartData: {
                labels: [],
                datasets: []
            },
            stackBarChartData: {
                datasets: [],
                labels: [],
                tooltip: []
            },
            filter: {
                timeFrame: 'day',
                criteriaId: ''
            },
            batches: [],
            batchActive: null,
            isLoadingOverviewTable: true,
            isLoadingLineChart: true,
            isLoadingStackBarChart: true,
            resultQCCampaignChartActive: {
                id: "result-by-time",
                type: 1,
                name: "Result QC campaign by call time"
            }
        }
    },
    computed: {
        ...mapState({
            criteriaList: state => state.qccampaign.campaign ? state.qccampaign.campaign.criteria.filter(i => (i.type === 'ENUM' || i.type === 'BOOL') && i.is_monitor) : [],
            permission: state => state.qccampaign.campaign.permission
        }),
        timeFrameOptions() {
            return [
                {
                    text: 'Day',
                    value: 'day'
                },
                {
                    text: 'Week',
                    value: 'week'
                },
                {
                    text: 'Month',
                    value: 'month'
                },
                {
                    text: 'Quarter',
                    value: 'quarter'
                }
            ]
        },
        resultQCCampaignCharts() {
            return [
                {
                    id: "result-by-time",
                    type: 1,
                    name: "Result QC campaign by call time"
                },
                {
                    id: "result-by-batch",
                    type: 2,
                    name: "Result QC campaign by batch"
                }
            ]
        }
    },
    mounted() {
        this.loadAllBatches().then(this.renderCharts)
    },
    methods: {
        renderCharts() {
            Promise.all([
                this.loadOverviewQCCampaignData(),
                //this.loadPendingTasksChartData(),
                this.loadResultQCChartData(),
            ]).then(() => {
                    if (this.$refs.LineChartPendingTasks) {
                        this.$refs.LineChartPendingTasks.render()
                    }
                    if (this.$refs.StackBarChartResultQC) {
                        this.$refs.StackBarChartResultQC.render()
                    }
                }
            )
        },
        loadOverviewQCCampaignData() {
            return new Promise((resolve, reject) => {
                this.isLoadingOverviewTable = true
                QcCampaignService.getOverviewQCCampaignData(this.$route.params.QCCampaignId).then(result => {
                    this.overviewData = result.data ? result.data : {}
                    resolve()
                }, error => {
                    reject(error)
                }).finally(() => {
                    this.isLoadingOverviewTable = false
                })
            })
        },
        loadPendingTasksChartData() {
            return new Promise((resolve, reject) => {
                this.isLoadingLineChart = true
                QcCampaignService.getPendingTasksChartData(this.$route.params.QCCampaignId, this.batchActive).then(result => {
                    this.lineChartData = {
                        datasets: [],
                        labels: [],
                        tooltip: []
                    }
                    let data = result.data.data_chart
                    if (data) {
                        let labels = []
                        data.map(i => i['update_time']).forEach(label => {
                            let arr = label.split(" ")
                            labels.push(`${arr[0]}h ${arr[1]}`)
                        })
                        let timeNow = this.$f.formatDateTimeLocal(new Date(), "HH DD-MM-YYYY").split(" ")
                        labels.push(`${timeNow[0]}h ${timeNow[1]}`)
                        this.lineChartData.labels = labels
                        let dataChart = data.map(i => i["count_pending"])
                        dataChart.push(dataChart[dataChart.length - 1])
                        let datasets = [{
                            label: 'Pending tasks',
                            data: dataChart,
                            borderColor: '#9AA9BF',
                            lineTension: 0,
                            fill: false,
                            pointBorderColor: 'rgba(0, 0, 0, 0)',
                            pointBackgroundColor: 'rgba(0, 0, 0, 0)',
                            pointHoverBackgroundColor: 'rgb(255, 99, 132)',
                            pointHoverBorderColor: 'rgb(255, 99, 132)'
                        }]
                        this.lineChartData.datasets = datasets
                    }
                    resolve()
                }, error => {
                    reject(error)
                }).finally(() => {
                    this.isLoadingLineChart = false
                })
            })
        },
        loadResultQCChartData() {
            if (this.permission === $const.QCCampaign.permission.QC_MEMBER) {
                return
            }
            return new Promise((resolve, reject) => {
                if (this.filter.criteriaId && this.filter.timeFrame) {
                    this.isLoadingStackBarChart = true
                    this.stackBarChartData = {
                        datasets: [],
                        labels: [],
                        tooltip: []
                    }
                    let filter = {
                        criteria_id: this.filter.criteriaId,
                        chart_type: this.resultQCCampaignChartActive.type
                    }
                    if (this.resultQCCampaignChartActive.type === 1) {
                        filter.filter_type = this.filter.timeFrame
                    }
                    QcCampaignService.getResultQCChartData(this.$route.params.QCCampaignId, filter).then(result => {
                        let data = result.data
                        if (data) {
                            let listTooltip = []
                            let keys = data.data_chart[0] ? Object.keys(data.data_chart[0]) : []
                            data.data_chart.forEach(item => {
                                let informationKey = []
                                keys.forEach(i => {
                                    informationKey.push({
                                        name: i.includes("$") ? this.$utils.convertSnakeToCapitalize(i.slice(1)) : i,
                                        value: item[i]
                                    })
                                })
                                listTooltip.push(informationKey)
                                if (this.resultQCCampaignChartActive.type === 1) {
                                    this.stackBarChartData.labels.push(item['$time'])
                                } else {
                                    this.stackBarChartData.labels.push(item['$batch_name'])
                                }
                            })
                            this.stackBarChartData.tooltip = listTooltip
                            let datasets = []
                            let resultTypes = data["result_types"]
                            resultTypes.forEach((nameType, index) => {
                                datasets.push({
                                    label: nameType,
                                    data: data.data_chart.map(i => i[nameType]),
                                    backgroundColor: this.getColorStackBarChart(index),
                                })
                            })
                            this.stackBarChartData.datasets = datasets
                        }
                        resolve()
                    }, error => {
                        reject(error)
                    }).finally(() => {
                        this.isLoadingStackBarChart = false
                    })
                } else {
                    this.isLoadingStackBarChart = false
                }
            })
        },
        loadAllBatches() {
            return QcCampaignService.getAllBatches(this.$route.params.QCCampaignId).then(result => {
                if (result.data.length > 0) {
                    this.batches = result.data
                    this.batchActive = this.batches[0].id
                } else {
                    this.resetDataCharts()
                }
            })
        },
        resetDataCharts() {
            this.overviewData = {}
            this.lineChartData = {
                labels: [],
                datasets: []
            }
            this.stackBarChartData = {
                datasets: [],
                labels: [],
                tooltip: []
            }
            this.isLoadingLineChart = false
            this.isLoadingOverviewTable = false
            this.isLoadingStackBarChart = false
        },
        getColorStackBarChart(index) {
            if (this.fixColorResultQCCampaign[index]) {
                return this.fixColorResultQCCampaign[index]
            } else {
                return this.$utils.getRandomColor()
            }
        },
    },
    watch: {
        criteriaList: {
            deep: true,
            handler: function () {
                if (this.criteriaList.length > 0) {
                    this.filter.criteriaId = this.criteriaList[0].id
                }
            }
        },
        filter: {
            deep: true,
            handler: function () {
                if (this.permission === $const.QCCampaign.permission.QC_MEMBER) {
                    return
                }
                this.loadResultQCChartData().then(() => {
                    if (this.$refs.StackBarChartResultQC) {
                        this.$refs.StackBarChartResultQC.render()
                    }
                })
            }
        },
        resultQCCampaignChartActive: {
            deep: true,
            handler: function () {
                this.loadResultQCChartData().then(() => {
                    if (this.$refs.StackBarChartResultQC) {
                        this.$refs.StackBarChartResultQC.render()
                    }
                })
            }
        },
        batchActive: 'loadPendingTasksChartData'
    }
}
</script>
<style lang="scss" scoped>
.label {
    font-size: 16px;
    height: 36px;
    line-height: 36px;
    font-family: "open sans semi";
}

.pending-tasks-chart {
    width: 65%;
    margin-left: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.empty-box {
    background: #f7f9fa;
    font-family: 'open sans';
    font-size: 14px;
    flex-direction: column;
    color: #6f7b90;
}

table.table-overview {
    border-collapse: collapse;
    font-size: 14px;
    width: 100%;

    tr {
        height: 40px;
    }

    th {
        padding: 0 10px;
    }

    td, th {
        border: 1px solid #DADFEC;
        height: 40px;
    }
}

table.table-inner {
    border-collapse: collapse;
    width: 100%;

    tr:last-child {
        td, th {
            border-bottom: none;
        }
    }

    td {
        border-right: none;
    }

    th {
        padding: 0 10px;
    }

    td, th {
        border-top: none;
        border-left: none;
    }
}

.active-chart {
    color: #0052cc;
    background: #e4edfa;
}

::v-deep {
    .v-input__slot {
        background: #FFFFFF;
        padding: 0 10px !important;
        border-radius: 0 !important;
        border: 1px solid #DADFEC !important;
        font-size: 14px !important;
        height: 36px !important;

        input::placeholder {
            color: #8A97AA;
            padding-top: 2px;
        }
    }

    .v-input input {
        height: 36px;
    }

    .v-input__slot:after, .v-input__slot:before {
        border: none !important;
    }

    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }
}
</style>
